import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  DialogProps,
  DialogTitle as MuiDialogTitle,
  styled,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import {
  ConnectedDeviceInfo,
  Contact,
  EnvironmentPurpose,
  EnvironmentWillAssign,
  Group,
} from '../../../../types';
import { DasWBGT } from '../../../../types/Device';
import { Environment } from '../../../../types/Resource';

import { getProjectDasWBGT } from '../../../../apis/DasWbgtApi';

import CheckListItem from '../../../../components/CheckListItem';

import CleanDateSvgIcon from '../../../../assets/SvgIcon/CleanDateSvgIcon';
import EditSvgIcon from '../../../../assets/SvgIcon/EditSvgIcon';
import {
  navigatorEnvironmant as navigator,
  parseDasId,
} from '../../../../utils/common';
import ConnectedDeviceView from '../../content/ConnectedDeviceView';
import ContactView from '../../content/ContactView';
import GroupView from '../../content/GroupView';
import PurposeAreaView from '../../content/PurposeAreaView';
import {
  checkArea,
  checkContactAvailable,
  checkEnvironmentBasicInformationAvailable,
} from '../../validation/environment-validation';
import { hasAssignedDevice } from '../../validation/management-validation';

import BasicInformationContent from './BasicInformationContent';

const Dialog = styled(MuiDialog)`
  color: white;
  & .MuiDialog-paper {
    width: 676px;
    height: 686px;
    max-width: unset;
    background: ${({ theme }) => theme.color.secondary.$80};
  }
`;

const Header = styled('div')`
  display: flex;
  align-items: center;
  color: white;
  padding: 8px 8px 8px 16px;
  height: 48px;
`;

const DialogTitle = styled(MuiDialogTitle)`
  ${({ theme }) => ({ ...theme.typography.h3 })}
  font-family: 'Montserrat';
  flex: 1;
  color: white;
  padding: 0;
`;

const ContentWrapper = styled('div')`
  height: calc(100% - 48px);
  width: 100%;
  display: flex;

  @media only screen and (max-width: 600px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const DialogContent = styled(MuiDialogContent)`
  padding: 10px 20px;
  @media only screen and (max-width: 600px) {
    min-height: 240px;
  }
`;

const ContainerSelected = styled('div')`
  width: 380px;
  border-radius: 4px;
  padding: 10px 20px;
`;

const CheckList = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
`;

const IconButtonStyled = styled('div')`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  transition: 0.3s;
  :hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

interface ViewEnvironmentProps extends DialogProps {
  projectId: string | undefined;
  title: string;
  navigatorIndex: number;
  selectedEnv: Environment | undefined;
  contacts: Array<Contact>;
  selectedGroups: Array<Group>;
  onCloseDialog: () => void;
  onClickEdit: () => void;
  onSelectNavigatorIndex: (index: number) => void;
}

const ViewEnvironment: React.FC<ViewEnvironmentProps> = ({
  open,
  projectId,
  title,
  navigatorIndex,
  selectedGroups,
  selectedEnv,
  contacts,
  onCloseDialog,
  onClickEdit,
  onSelectNavigatorIndex,
}) => {
  const { projectId: routeProjectId } = useParams();
  const resolvedProjectId = projectId ?? routeProjectId;

  const [checkedValue, setCheckedValue] = useState(
    new Array(navigator.length).fill(false),
  );
  const { t } = useTranslation();

  useEffect(() => {
    const updateChecked = checkedValue.map((_, index) =>
      index === navigatorIndex ? true : false,
    );
    setCheckedValue(updateChecked);
  }, [navigatorIndex]);

  const environmentPurpose = useMemo<EnvironmentPurpose | undefined>(() => {
    const bindingDevices = selectedEnv?.bindingDevices;
    if (!bindingDevices || bindingDevices.length === 0) return 'confined-space';
    const hasWBGT = bindingDevices.some((d) => parseDasId(d) === 'daswbgt');
    return hasWBGT ? 'heatstroke-system' : 'confined-space';
  }, [selectedEnv]);

  useEffect(() => {
    if (environmentPurpose !== 'heatstroke-system') {
      const connectedDeviceIndex = navigator.findIndex(
        (item) => item.name === 'connected-device',
      );
      if (navigatorIndex === connectedDeviceIndex) {
        onSelectNavigatorIndex(connectedDeviceIndex - 1);
      }
    }
  }, [environmentPurpose]);

  const { data: assignedDasWBGT } = useQuery({
    queryKey: ['assigned-daswbgts', resolvedProjectId, selectedEnv?.id],
    queryFn: async () => {
      const dasWBGTs: DasWBGT[] = [];
      const bindingDevice = selectedEnv?.bindingDevices;
      if (bindingDevice) {
        for (let i = 0; i < bindingDevice.length; i++) {
          const res = await getProjectDasWBGT({
            projectId: resolvedProjectId as string,
            params: { dasId: bindingDevice[i], limit: 1 },
          });
          if (res.data.data.length === 1) {
            dasWBGTs.push(res.data.data[0]);
          }
        }
      }
      return dasWBGTs;
    },
    enabled: !!resolvedProjectId && !!selectedEnv,
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const listConnected = useMemo<ConnectedDeviceInfo[]>(() => {
    return assignedDasWBGT.map((device) => ({
      dasId: device.dasId,
      name: device.dasId,
    }));
  }, [assignedDasWBGT]);

  return (
    <Dialog open={open}>
      <Header>
        <DialogTitle>{title}</DialogTitle>
        <IconButtonStyled
          onClick={() => {
            onCloseDialog();
            onClickEdit();
          }}
          data-cy="dialog-edit-environment"
        >
          <EditSvgIcon
            sx={{ width: '32px', height: '32px', cursor: 'pointer' }}
          />
        </IconButtonStyled>
        <CleanDateSvgIcon
          sx={{ width: '32px', height: '32px', cursor: 'pointer' }}
          onClick={onCloseDialog}
        />
      </Header>

      <ContentWrapper>
        <DialogContent>
          <CheckList>
            {navigator.map((item, index) => {
              if (
                item.name === 'connected-device' &&
                environmentPurpose !== 'heatstroke-system'
              ) {
                return null;
              }
              let checked: boolean = false;
              switch (item.name) {
                case 'group':
                  checked = true;
                  break;
                case 'environment-basic-information':
                  checked = checkEnvironmentBasicInformationAvailable(
                    selectedEnv as EnvironmentWillAssign,
                  );
                  break;
                case 'purpose-area':
                  checked =
                    checkArea(selectedEnv?.areaId) && !!environmentPurpose;
                  break;
                case 'emergency-contact':
                  checked = checkContactAvailable(contacts.map((cp) => cp.id));
                  break;
                case 'connected-device':
                  checked = hasAssignedDevice(
                    listConnected.map((d) => ({
                      id: d.dasId,
                      dasId: d.dasId,
                      name: d.name,
                      batteryLevel: 0,
                    })),
                  );
                  break;
              }

              return (
                <CheckListItem
                  key={item.id}
                  name={t(`project-settingJson.navigator.${item.name}`)}
                  required={item.required}
                  checked={checked}
                  onClick={() => onSelectNavigatorIndex(index)}
                  selected={checkedValue[index]}
                />
              );
            })}
          </CheckList>
        </DialogContent>
        <ContainerSelected>
          {navigator[navigatorIndex]?.name === 'group' && (
            <GroupView group={selectedGroups} resource="environment" />
          )}
          {navigator[navigatorIndex]?.name ===
            'environment-basic-information' && (
            <BasicInformationContent data={selectedEnv} />
          )}
          {navigator[navigatorIndex]?.name === 'purpose-area' && (
            <PurposeAreaView
              projectId={resolvedProjectId}
              selectedAreaId={selectedEnv?.areaId}
              selectedPurpose={environmentPurpose}
            />
          )}
          {navigator[navigatorIndex]?.name === 'emergency-contact' && (
            <ContactView data={contacts} />
          )}
          {navigator[navigatorIndex]?.name === 'connected-device' &&
            environmentPurpose === 'heatstroke-system' && (
              <ConnectedDeviceView
                data={listConnected}
                resource="environment"
                projectId={resolvedProjectId}
              />
            )}
        </ContainerSelected>
      </ContentWrapper>
    </Dialog>
  );
};

export default ViewEnvironment;
